import { getDocumentLang } from "./helpers";

const getAuthHeaders = (extra = {}) => {
  const headers = {
    Accept: "text/event-stream",
    "Accept-Language": getDocumentLang() || "en",
    ...extra,
  };
  const token = localStorage.getItem("token");
  if (token) headers.Authorization = token;
  const country = localStorage.getItem("country");
  if (country) headers["X-Country-Code"] = country;
  return headers;
};

const apiBase = () => process.env.REACT_APP_API_URL || "";

/**
 * Parse SSE chunks. Each `data:` payload is JSON (string token or { done: true }).
 * Returns { completed: boolean } — completed only when { done: true } was received.
 */
export async function consumeSseStream(response, { onToken, signal } = {}) {
  if (!response.ok) {
    let body = null;
    try {
      body = await response.json();
    } catch {
      body = null;
    }
    const error = new Error(
      body?.error || body?.message || `Request failed (${response.status})`,
    );
    error.status = response.status;
    error.data = body;
    throw error;
  }

  const contentType = response.headers.get("content-type") || "";

  // Cache-hit style JSON response (e.g. POST actions with cached: true)
  if (contentType.includes("application/json")) {
    const json = await response.json();
    return { completed: true, json };
  }

  if (!response.body) {
    const error = new Error("No response body");
    error.status = response.status;
    throw error;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let completed = false;

  try {
    while (true) {
      if (signal?.aborted) {
        await reader.cancel();
        break;
      }
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        const raw = line.slice(6).trim();
        if (!raw) continue;

        let payload;
        try {
          payload = JSON.parse(raw);
        } catch {
          continue;
        }

        if (payload && typeof payload === "object" && payload.done === true) {
          completed = true;
          break;
        }

        if (typeof payload === "string") {
          onToken?.(payload);
        }
      }

      if (completed) break;
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* ignore */
    }
  }

  if (!completed && !signal?.aborted) {
    const error = new Error("Stream ended before completion");
    error.incomplete = true;
    throw error;
  }

  return { completed };
}

export async function streamPost(path, body, { onToken, signal, language } = {}) {
  const headers = getAuthHeaders({
    "Content-Type": "application/json",
  });
  if (language) headers["Accept-Language"] = language;

  const response = await fetch(`${apiBase()}${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    signal,
  });

  return consumeSseStream(response, { onToken, signal });
}

export async function streamGet(path, { onToken, signal, language } = {}) {
  const headers = getAuthHeaders();
  if (language) headers["Accept-Language"] = language;

  const response = await fetch(`${apiBase()}${path}`, {
    method: "GET",
    headers,
    signal,
  });

  return consumeSseStream(response, { onToken, signal });
}

export function buildLessonChatStreamUrl(lessonId, message, timestamp) {
  const params = new URLSearchParams({ message });
  if (timestamp != null && !Number.isNaN(Number(timestamp))) {
    params.set("timestamp", String(timestamp));
  }
  return `/api/v1/user/ai/lessons/${lessonId}/chat/stream?${params.toString()}`;
}
